import React, { useEffect, useMemo } from "react";
import { tt } from "../../utils/i18n/i18n";
import { setLoading, useDateRangeSelector } from "../../redux/actions/app";
import { useAccountToDisplaySelector } from "../../redux/actions/user";
import fetchDashboardAnalytics from "../../api/fetchDashboardAnalytics";
import TSUtils from "../../utils/helpers/TSUtils";
import { DEFAULT_ANALYTICS_DATA } from "./model/AnalyticsData";
import DateRangePickerReduxWrapper from "../../components/DateRangePickerReduxWrapper";
import AnalyticsPlaceholder from "./components/AnalyticsPlaceholder";
import KC_Separator from "../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../designsystem/ds_commons";
import { useQuery } from "react-query";
import { DEFAULT_STALE_CACHE_TIME } from "../../utils/constants";
import {
  DEFAULT_CURRENCY_SUB_UNITS,
  DEFAULT_CURRENCY
} from "@tootsweet/model/enums/Currencies";
import TwemojiWrapper from "../../components/TwemojiWrapper/TwemojiWrapper";
import KC_Heading2 from "../../designsystem/typography/KC-Heading/KC_Heading2";
import KC_UIText from "../../designsystem/typography/KC_UIText/KC_UIText";
import { useDispatch } from "react-redux";
import KC_DashboardCard from "../../designsystem/KC_Card/KC_DashboardCard";
import RepeatBusiness from "./components/dashboard_items/RepeatBusiness";
import KC_Heading4 from "../../designsystem/typography/KC-Heading/KC_Heading4";
import Referral from "./components/dashboard_items/Referral";
import FavoriteGamesAndGifts from "./components/dashboard_items/FavoriteGamesAndGifts";
import CTAs from "./components/dashboard_items/CTAs";
import Players from "./components/dashboard_items/Players";
import TopLineStatistics from "./components/dashboard_items/TopLineStatistics";
import KC_Padder from "../../designsystem/KC_Padder/KC_Padder";
import DailyStatistics from "./components/dashboard_items/DailyStatistics";
import CurrencyProp from "./components/model/CurrencyProp";
import AvgBasketPromptOverlay from "./components/AvgBasketPromptOverlay";
import { ManagedAccountDropdown } from "../../components/ManagedAccountDropdown/ManagedAccountDropdown";
import "./styles.scss";

export const Analytics = () => {
  const dispatch = useDispatch();
  const dateRange = useDateRangeSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const currency = useMemo<CurrencyProp>(() => {
    return {
      trigram:
        accountToDisplay?.dCurrency ||
        accountToDisplay?.currency ||
        DEFAULT_CURRENCY,
      subUnits: accountToDisplay?.curSubUnits || DEFAULT_CURRENCY_SUB_UNITS
    };
  }, [accountToDisplay]);

  const { data, isLoading, isRefetching, isFetching } = useQuery({
    queryKey: [
      "dashboardAnalytics",
      accountToDisplay?.trackingID,
      dateRange.start,
      dateRange.end,
      accountToDisplay?.avgBasket,
      accountToDisplay?.bizContrib
    ],
    queryFn: async () => {
      if (!accountToDisplay?.trackingID || !accountToDisplay.token) {
        return DEFAULT_ANALYTICS_DATA;
      }

      const data = await fetchDashboardAnalytics(
        accountToDisplay.trackingID,
        TSUtils.dateToTimestamp(dateRange.start),
        TSUtils.dateToTimestamp(dateRange.end),
        false,
        accountToDisplay?.avgBasket || 0,
        accountToDisplay?.bizContrib || 0,
        accountToDisplay.token,
        currency.subUnits
      );

      return data.payload;
    },
    enabled:
      !!accountToDisplay &&
      !!accountToDisplay.token &&
      !!accountToDisplay.trackingID &&
      !!accountToDisplay.avgBasket,
    onError: (err) => {
      console.error(err);
      alert(tt("api_error"));
    },
    staleTime: DEFAULT_STALE_CACHE_TIME
  });

  function renderToolbar() {
    return (
      <div className="d-flex flex-row align-items-center justify-content-center my-4">
        <ManagedAccountDropdown />
        <div className="flex-grow-1"></div>
        {renderDateSelector()}
      </div>
    );
  }

  useEffect(() => {
    dispatch(setLoading(isLoading || isFetching || isRefetching));
  }, [isLoading, isFetching, isRefetching]);

  if (!accountToDisplay) {
    return <AnalyticsPlaceholder />;
  }

  if (
    accountToDisplay.avgBasket &&
    (!data?.hasData || data?.nActivePlayers < 1)
  ) {
    return (
      <div className="d-flex flex-column flex-grow-1 p-4 dashboard-v2">
        {renderToolbar()}
        {!isLoading && (
          <>
            <KC_Separator size={KC_Sizes.m} />
            <div className="d-flex flex-grow-1 flex-column justify-content-center align-items-center st-gap">
              <TwemojiWrapper isVeryBigText={true}>🤖</TwemojiWrapper>
              <KC_Heading2 text={tt("no_data")} className="text-center" />
              <KC_UIText
                weight="md"
                text={tt("analytics_no_players_yet")}
                className="text-center"
              />
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-grow-1 dashboard-v2 px-4 position-relative">
      <AvgBasketPromptOverlay
        accountToDisplay={accountToDisplay}
        currency={currency}
      >
        {renderToolbar()}
        <TopLineStatistics data={data} currency={currency} />
        <DailyStatistics />
        <Players data={data} />
        <CTAs data={data} />
        <Referral data={data} />
        <RepeatBusiness
          data={data}
          currency={currency}
          accountToDisplay={accountToDisplay}
        />
        <FavoriteGamesAndGifts data={data} />
        <div className="row mt-5">
          <div className="col-12">
            <KC_DashboardCard title={tt("analytics_email_performance")}>
              <KC_Heading4 text={tt("analytics_coming_soon")} primary={true} />
            </KC_DashboardCard>
          </div>
        </div>

        <KC_Padder />
      </AvgBasketPromptOverlay>
    </div>
  );
};

function renderDateSelector() {
  return (
    <div className="d-flex flex-row justify-content-center justify-content-lg-end px-4 py-4 p-lg-0 k-date-picker-container">
      <DateRangePickerReduxWrapper />
    </div>
  );
}
